import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PaymentRecoveryBanner from "@/components/PaymentRecoveryBanner";
import ReferralCreditsCard from "@/components/ReferralCreditsCard";
import CompBanner from "@/components/CompBanner";
import IapPaywall from "@/components/IapPaywall";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { usePublicPrices } from "@/lib/usePublicPrices";
import { useBillingCatalog } from "@/lib/useBillingCatalog";
import { ReaderAppBanner, useReaderAppMode } from "@/components/ReaderAppBanner";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import { formatApiError } from "@/lib/utils-app";
import { CrownIcon as Crown, PawPrintIcon as PawPrint, HouseSimpleIcon as HouseSimple, SparkleIcon as Sparkle, ShieldCheckIcon as ShieldCheck, CertificateIcon as Certificate, VideoCameraIcon as VideoCamera, StorefrontIcon as Storefront, HeartIcon as Heart, ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, StarIcon as Star, CaretRightIcon as CaretRight, CreditCardIcon as CreditCard, TagIcon as Tag, InfoIcon as Info, TrendUpIcon as TrendUp, CheckSquareIcon as CheckSquare, XIcon as X, CaretDownIcon as CaretDown, LightningIcon as Lightning, CoffeeIcon as Coffee, TrophyIcon as Trophy, EnvelopeIcon as Envelope, ReceiptIcon as Receipt, WarningIcon as Warning } from "@phosphor-icons/react";

/**
 * Simplified billing page — same heritage-luxury visual language as
 * /services so the upgrade experience feels consistent end-to-end.
 *
 * Above-the-fold the page is two cards:
 *   1. ActiveSubscriptionBanner — what the user is currently paying
 *      for, including the renewal date and a "Manage" deep-link to
 *      Stripe Customer Portal.
 *   2. RecommendedUpgrade — the single "next step up" tier for the
 *      user, with a 1-click checkout button.
 *
 * Below the fold sit the catalog blocks (subscriptions, add-ons,
 * verification & trust) wired to checkout. Every card has a single
 * CTA — no per-card toggle for monthly/annual; that lives at the top
 * of the subscription grid like /services.
 *
 * Heavy comparison tables, the persona toggle, and the plan quiz
 * (previously cluttering this surface) have been removed. Buyers who
 * want a deep comparison go to /services or /features for the
 * inventory.
 */

// SKU catalogue mirrors backend/routers/billing/packages.py. We don't
// fetch from /api/billing/plans because we want this page to render
// instantly without a roundtrip — the canonical price source is the
// backend PACKAGES dict and is enforced at checkout time anyway.
const SUBSCRIPTIONS = [
  {
    id: "buyer_pass",
    icon: PawPrint,
    title: "Buyer Pass",
    tag: "For buyers",
    monthly: 4.99,
    annual: 39,
    monthly_pkg: "buyer_pass_monthly",
    annual_pkg: "buyer_pass_annual",
    pitch: "Shopping for your next dog? This unlocks unlimited messages to breeders and shows you the full pedigree on every dog you're considering.",
    features: [
      "Message any breeder, as many times as you want",
      "Save kennels and litters to a personal watchlist",
      "See full 6-generation family trees on every dog",
      "Get email alerts when a breed you want is available",
    ],
  },
  {
    id: "care_plus_dog_log",
    icon: Heart,
    title: "Dog Log",
    tag: "For dog lovers",
    monthly: 9.99,
    annual: 69,
    monthly_pkg: "care_plus_monthly",
    annual_pkg: "care_plus_annual",
    pitch: "A simple daily diary for your dog — track meals, walks, weight, and meds. Your vet will love how organized you suddenly are.",
    features: [
      "One-tap logging for meals, walks, potty, meds, weight & more",
      "Warns you when symptoms keep repeating — catch issues early",
      "Share a live view with your pet sitter (no app to install)",
      "One-click PDFs for vet visits and pet-sitter handoffs",
      "Free with Breeder Pro and Kennel Elite",
    ],
  },
  {
    id: "basic_plan",
    icon: HouseSimple,
    title: "Breeder Basic",
    tag: "For breeders",
    monthly: 19,
    annual: 190,
    monthly_pkg: "basic_plan_v2",
    annual_pkg: "basic_plan_v2_annual",
    pitch: "Just getting started or doing 1–2 litters a year? This plan gives you a real registry presence without paying for things you don't need yet.",
    anchor: "Need unlimited registrations? Step up to Pro ($44/mo) — pays for itself after 2–3 marketplace sales.",
    features: [
      "Register up to 10 dogs and 4 litters per month",
      "Free single-dog listings on the marketplace (5 active at once)",
      "2 free 30-day breeding-directory listings per month (then pay-as-you-go)",
      "Heat-cycle tracker with breeding-window reminders",
      "Your own public kennel page that buyers can find on Google",
      "Auto-made share cards for every dog you list — post straight to social",
    ],
  },
  {
    id: "pro_plan",
    icon: Crown,
    title: "Breeder Pro",
    tag: "Most popular",
    highlight: true,
    monthly: 44,
    annual: 420,
    monthly_pkg: "pro_plan_v2",
    annual_pkg: "pro_plan_v2_annual",
    pitch: "For working kennels doing 3–8 litters a year. Register as many dogs as you want and keep more of every sale you make.",
    features: [
      "Register unlimited dogs and litters — no caps",
      "Unlimited marketplace listings + unlimited Planned Litter posts",
      "Unlimited breeding-directory listings at every tier (30/90/365-day) — free",
      "Pedigree certificates included free ($14.99 each value)",
      "Gold Verified Pro seal on every listing — buyers trust it on sight",
      "Built-in Dog Log + breeder analytics (which listings convert, where leads come from)",
      "You pay 9% on each sale (instead of 10%) — most breeders make the subscription back in 2 sales",
    ],
  },
  {
    id: "kennel_elite",
    icon: Star,
    title: "Kennel Elite",
    tag: "Pays for itself",
    monthly: 129,
    annual: 1290,
    monthly_pkg: "kennel_elite_v2",
    annual_pkg: "kennel_elite_v2_annual",
    pitch: "For serious programs running multiple handlers. The lowest platform fee anywhere, top placement in search, and the marketing tools to win the buyers who are still deciding.",
    features: [
      "Everything in Breeder Pro — plus the heaviest perks",
      "Just 8% on every sale (save another 1% over Pro on every dog you ship)",
      "Unlimited DNA-Verified Registrations included (a $75 value per dog)",
      "Unlimited breeding-directory listings + featured top placement + auto-renew on expiry",
      "Top placement in the marketplace — your kennel rotates into the featured spotlight",
      "See which marketing channels bring real buyers (lead-source attribution)",
      "Automatic waitlist follow-up emails so prospects don't go cold",
      "Reputation defense: reply directly under every public review",
      "Video kennel-tour banner + pinned testimonials + award showcase on your page",
      "Add 2 staff sub-accounts so your team can help post and reply",
    ],
  },
  {
    id: "storefront",
    icon: Storefront,
    title: "The Dog Shop",
    tag: "Sell direct",
    monthly: 19,
    annual: 179,
    monthly_pkg: "storefront_monthly",
    annual_pkg: "storefront_annual",
    pitch: "Already selling food, supplements, or merch on the side? Open your own little store page and have buyers pay you directly — we never touch the money.",
    features: [
      "Your own /store/your-kennel-name page with custom branding",
      "Sell dog food, supplements, and merch in one catalog",
      "Set a local delivery zone by ZIP — no shipping nightmares",
      "Buyers pay you directly via Stripe (we never hold the funds)",
      "Just 5% per sale, no other markup or hidden fees",
    ],
  },
];

const VERIFICATION = [
  {
    id: "kennel_verification",
    pkg: "kennel_verification",
    icon: ShieldCheck,
    title: "Verified Kennel Badge",
    price: 79,
    cadence: "one-time",
    pitch: "We verify your ID and business with you once. A gold badge then shows up next to your name everywhere — buyers see it on every listing and know you're real.",
  },
  {
    id: "elite_verified_annual",
    pkg: "elite_verified_annual",
    icon: Sparkle,
    title: "Verified Kennel — annual prestige",
    price: 199,
    cadence: "per year",
    pitch: "An annual prestige tier on top of the Verified badge. Your kennel jumps to the top of marketplace search and gets a featured slot in the public kennel directory hero. Distinct from the Kennel Elite plan — this is a verification badge, not a plan.",
  },
];

// À la carte items. The `route` field is critical: every item here is
// contextual (requires a specific dog/litter/event id) so we cannot
// trigger a Stripe checkout from /billing. Each card routes to the
// page where the user can actually pick the target object and then
// initiate checkout from *there* with the correct payload.
const ADDONS = [
  { id: "dog_registration",       pkg: "dog_registration",       icon: Certificate, title: "Register a dog",            price: 25,     cadence: "per dog",      pitch: "Adds a dog to the registry for life. Includes a unique AST###### number, a free Certificate of Registration, and an in-built family-tree calculator (COI) so buyers know exactly how inbred a dog is.",                 route: "/dogs/new",          cta: "Register a dog" },
  { id: "litter_registration",    pkg: "litter_registration",    icon: Certificate, title: "Register a litter",         price: 75,     cadence: "per litter",   pitch: "Adds sire, dam, and every puppy in one flow. Each puppy gets its own registry number and is ready to list on the marketplace the same day.",                       route: "/litters/new",       cta: "Register a litter" },
  { id: "certificate_purchase",   pkg: "certificate_purchase",   icon: Certificate, title: "Pedigree certificate",      price: 14.99,  cadence: "per dog",      pitch: "A printable, heritage-paper PDF showing the dog's full pedigree — buyers love the keepsake. Free if you're on the Pro plan.",                  route: "/dogs",              cta: "Pick a dog",            requires: "dog" },
  { id: "video_tour",             pkg: "video_tour",             icon: VideoCamera, title: "Live video tour",           price: 35,     cadence: "15-min",       pitch: "Schedule a 15-minute browser video call to walk a buyer through your kennel and dogs. You only get charged when the breeder accepts — and 60–80% of the fee goes to them.",     route: "/marketplace",       cta: "Browse breeders" },
  { id: "dog_marketplace_listing",pkg: "dog_marketplace_listing",icon: Storefront,  title: "List a dog on marketplace", price: 9.99,   cadence: "60 days",      pitch: "Put one dog in front of every buyer browsing the site for 60 days. Free with any Breeder plan.",                                   route: "/dogs",              cta: "Pick a dog to list",    requires: "dog" },
  { id: "litter_marketplace_listing", pkg: "litter_marketplace_listing", icon: Storefront, title: "List a litter on marketplace", price: 19.99, cadence: "60 days", pitch: "List every puppy in a litter on the same page so buyers can compare and pick. Free with Pro.",          route: "/litters",           cta: "Pick a litter",         requires: "litter" },
  { id: "marketplace_feature",    pkg: "marketplace_feature",    icon: Star,        title: "Featured listing",          price: 29,     cadence: "30 days",      pitch: "Pins your listing to the very top of the marketplace for 30 days — the kind of placement that usually sells the dog or litter inside a week.",                            route: "/dogs",              cta: "Pick a listing",        requires: "dog" },
  { id: "breeding_listing_30d",   pkg: "breeding_listing_30d",   icon: Heart,       title: "Breeding directory listing",price: 14.99,  cadence: "from",         pitch: "Lists your stud or female in the public breeding directory so other breeders can find a match. 30 / 90 / 365-day tiers available. Basic plan: first 2 free per month. Pro & Elite: unlimited free — Elite also gets featured top placement and auto-renew.",                route: "/dogs",              cta: "Pick a dog",            requires: "dog" },
  { id: "upcoming_litter_planned",    pkg: "upcoming_litter_planned",    icon: Heart,           title: "Plan an upcoming litter",   price: 25,     cadence: "per planned breeding",    pitch: "Publish a sire × dam pairing BEFORE the breeding happens. Start collecting waitlist deposits today — buyers pay you directly, you keep the relationship.",                  route: "/litters/upcoming/new", cta: "Plan a litter" },
  { id: "upcoming_litter_expecting",  pkg: "upcoming_litter_expecting",  icon: Heart,           title: "Expected litter listing",   price: 40,     cadence: "once breeding confirmed", pitch: "Already confirmed the breeding took? Upgrade the listing so buyers can see ultrasound dates and expected whelp window — adds urgency to the waitlist.",            route: "/litters/upcoming/new", cta: "List expecting" },
  { id: "transfer_ownership",     pkg: "transfer_ownership",     icon: ArrowsLeftRight, title: "Ownership transfer",    price: 15,     cadence: "per transfer", pitch: "Officially hands a dog over to its new owner in the registry. Chain-of-title is recorded, so years from now anyone can prove who owned the dog when.",                      route: "/dogs",              cta: "Pick a dog to transfer",requires: "dog" },
];


function fmtUSD(p) {
  return Number.isInteger(p) ? `$${p}` : `$${p.toFixed(2)}`;
}

function planTier(planKey) {
  // 0 = none, 1 = basic, 2 = pro, 3 = elite. Used to compute the next
  // recommended upgrade for breeders.
  return { kennel_elite: 3, pro: 2, basic: 1 }[planKey] || 0;
}


/** -------------------- RenewalNoticePreview --------------------
 * Shows the user exactly what their next auto-renewal notice email
 * will say. Mirrors the legally-required ROSCA / CA Auto-Renewal Law
 * disclosure we mail 7 business days before each charge — surfacing
 * it on /billing turns a "regulatory tax" into a self-service
 * billing console.
 */
function fmtLongDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return String(iso).slice(0, 10); }
}

function RenewalNoticePreview({ previews, onCancel, portalLoading }) {
  if (!previews || previews.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-[#E8D9A6] bg-cream/60 p-5 sm:p-6 mb-8"
      data-testid="billing-renewal-preview"
    >
      <div className="flex items-start gap-3 mb-4">
        <Envelope size={22} weight="duotone" className="text-gold-dark mt-0.5" />
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark">
            Auto-renewal disclosure
          </p>
          <h3 className="font-serif text-xl text-ink mt-1">
            Your next renewal notice
          </h3>
          <p className="text-[13px] text-[#6B6B6B] mt-1 leading-relaxed">
            Federal law (FTC ROSCA) and California's Auto-Renewal Law require
            us to email you before each charge. Here's exactly what's coming,
            so nothing on your bill is a surprise.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {previews.map((p) => (
          <RenewalNoticeCard
            key={p.subscription_id}
            preview={p}
            onCancel={onCancel}
            portalLoading={portalLoading}
          />
        ))}
      </div>
    </section>
  );
}

function RenewalNoticeCard({ preview, onCancel, portalLoading }) {
  const {
    plan_label, interval_label, amount_usd, renews_on,
    notice_will_be_sent_on, card_last4, will_renew, subscription_id,
  } = preview;

  const willRenew = !!will_renew;
  const amountFmt = `$${Number(amount_usd || 0).toFixed(2)}`;

  return (
    <div
      className="rounded-xl bg-white border border-[#EAE0C2] p-4 sm:p-5"
      data-testid={`renewal-card-${subscription_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-serif text-lg text-ink">{plan_label}</h4>
            {willRenew ? (
              <Badge className="bg-[#0F8043]/10 text-[#0F8043] border border-[#0F8043]/20 text-[10px] uppercase tracking-wider font-semibold">
                Auto-renews
              </Badge>
            ) : (
              <Badge className="bg-[#C1272D]/10 text-[#C1272D] border border-[#C1272D]/20 text-[10px] uppercase tracking-wider font-semibold">
                <Warning size={11} weight="bold" className="mr-1" />
                Won't renew
              </Badge>
            )}
          </div>
          {willRenew ? (
            <p className="text-[13px] text-[#4A4A4A] mt-1">
              We'll charge <strong className="text-ink">{amountFmt}</strong>
              {interval_label ? <> · {interval_label.toLowerCase()}</> : null}
              {" "}on <strong className="text-ink">{fmtLongDate(renews_on)}</strong>
              {card_last4 ? <> to the card ending <strong className="text-ink">•••• {card_last4}</strong></> : null}.
            </p>
          ) : (
            <p className="text-[13px] text-[#4A4A4A] mt-1">
              No future charges. Your access continues through{" "}
              <strong className="text-ink">{fmtLongDate(renews_on)}</strong>.
            </p>
          )}
        </div>
        {willRenew && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={portalLoading}
            className="rounded-full border-[#C1272D]/40 text-[#C1272D] hover:bg-[#C1272D] hover:text-white hover:border-[#C1272D] text-[12.5px] font-semibold"
            data-testid={`renewal-cancel-${subscription_id}`}
          >
            {portalLoading ? "Opening…" : "Cancel before next charge"}
          </Button>
        )}
      </div>

      {willRenew && (
        <div className="mt-4 pt-3 border-t border-[#F0EAD6] flex items-start gap-2.5 text-[12.5px] text-[#6B6B6B]">
          <Receipt size={15} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
          <span>
            We'll email you a reminder to your registered address on{" "}
            <strong className="text-ink">{fmtLongDate(notice_will_be_sent_on)}</strong>
            {" "}— that's 7 business days before the charge. See the{" "}
            <Link to="/legal/auto-renew" className="underline decoration-gold underline-offset-2 hover:text-ink">
              full disclosure
            </Link>.
          </span>
        </div>
      )}
    </div>
  );
}




/** -------------------- SavingsOffers --------------------
 * Two server-computed banners from GET /billing/savings-offers:
 *  1. Annual switch — monthly subscriber whose annual sibling is cheaper.
 *  2. Plan math — à-la-carte breeder whose 12-month spend already
 *     exceeds an annual plan price.
 * Apple-IAP subscriptions are excluded server-side, and iOS native
 * users never reach this page (redirected to /account/subscription),
 * so every CTA here is a web/Stripe checkout — compliance-safe.
 */
function SavingsOffers({ onBuy, checkoutLoading }) {
  const [offers, setOffers] = useState(null);
  useEffect(() => {
    api.get("/billing/savings-offers").then((r) => setOffers(r.data)).catch(() => {});
  }, []);
  if (!offers) return null;
  const a = offers.annual_switch;
  const m = offers.plan_math;
  if (!a && !m) return null;
  const browserSuffix = isNativeApp() ? " — in browser" : "";
  return (
    <div className="space-y-4 mb-8" data-testid="savings-offers">
      {a && (
        <div
          className="rounded-2xl border border-[#E5D08A] bg-gradient-to-br from-[#FFFBEF] to-[#FBF3D9] p-5 sm:p-6"
          data-testid="annual-savings-banner"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-[240px]">
              <TrendUp size={24} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark">
                  You're leaving money on the table
                </p>
                <h3 className="font-serif text-xl text-ink mt-1">
                  Save {fmtUSD(a.save_usd)}/yr by switching to annual
                </h3>
                <p className="text-[13px] text-[#6B6B6B] mt-1 leading-relaxed">
                  You're paying {fmtUSD(a.monthly_price)}/mo for {a.plan_label} —
                  that's {fmtUSD(a.yearly_cost_on_monthly)} a year. The annual plan is{" "}
                  {fmtUSD(a.annual_price)} flat: same features, {a.save_pct}% less,
                  and your price is locked in.
                </p>
              </div>
            </div>
            <Button
              onClick={() => onBuy(a.annual_package_id)}
              disabled={checkoutLoading === a.annual_package_id}
              className="rounded-full bg-ink text-white hover:bg-[#C1272D] font-semibold px-6"
              data-testid="annual-savings-cta"
            >
              {checkoutLoading === a.annual_package_id
                ? "Loading…"
                : `Switch & save ${fmtUSD(a.save_usd)}${browserSuffix}`}
            </Button>
          </div>
        </div>
      )}
      {m && (
        <div
          className="rounded-2xl border border-[#E8E0CC] bg-white p-5 sm:p-6"
          data-testid="plan-math-banner"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-[240px]">
              <Sparkle size={24} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark">
                  Your numbers, not ours
                </p>
                <h3 className="font-serif text-xl text-ink mt-1">
                  A plan is now cheaper than paying per-dog
                </h3>
                <p className="text-[13px] text-[#6B6B6B] mt-1 leading-relaxed">
                  You've spent {fmtUSD(m.spend_usd)} across {m.n_payments} à-la-carte
                  purchases in the last 12 months. {m.plan_label} is{" "}
                  {fmtUSD(m.plan_price)} — the same activity would have saved you{" "}
                  <strong className="text-ink">{fmtUSD(m.save_usd)}</strong>.
                </p>
              </div>
            </div>
            <Button
              onClick={() => onBuy(m.recommended_package_id)}
              disabled={checkoutLoading === m.recommended_package_id}
              className="rounded-full bg-ink text-white hover:bg-[#C1272D] font-semibold px-6"
              data-testid="plan-math-cta"
            >
              {checkoutLoading === m.recommended_package_id
                ? "Loading…"
                : `Get ${m.plan_label.split(" — ")[0]}${browserSuffix}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/** -------------------- ActiveSubscriptionBanner -------------------- */
function ActiveSubscriptionBanner({ sub, buyerPass, onPortal, portalLoading }) {
  const hasSub = sub && sub.plan;
  const hasPass = buyerPass && !buyerPass.admin_override;
  if (!hasSub && !hasPass) {
    return (
      <div
        className="rounded-2xl border border-[#E8E0CC] bg-white p-6 mb-8"
        data-testid="billing-no-active-banner"
      >
        <div className="flex items-center gap-3 mb-2">
          <Info size={22} weight="duotone" className="text-gold-dark" />
          <h2 className="font-serif text-xl text-ink">No active subscription</h2>
        </div>
        <p className="text-sm text-[#4A4A4A]">
          You're on the free tier. Pick a plan below to unlock unlimited inquiries, free certificates, or pro breeding tools.
        </p>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-ink to-[#1f1f1f] text-white p-6 mb-8 shadow-[0_24px_60px_rgba(14,14,14,0.25)]"
      data-testid="billing-active-banner"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gold-light mb-2">
            Active subscription
          </p>
          {hasSub && (
            <div className="mb-3">
              <div className="flex items-center gap-2.5">
                <CheckCircle size={22} weight="fill" className="text-gold" />
                <h2 className="font-serif text-2xl">
                  {sub.plan === "kennel_elite" ? "Kennel Elite"
                    : sub.plan === "pro" ? "Breeder Pro"
                    : sub.plan === "storefront" ? "The Dog Shop"
                    : "Breeder Basic"}
                </h2>
              </div>
              <p className="text-[13px] text-white/80 mt-1">
                Renews {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}
              </p>
              {sub.plan === "storefront" && (
                <Link
                  to="/me/store"
                  className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-gold-gradient text-ink font-semibold text-[12.5px] px-4 py-2 hover:brightness-110"
                  data-testid="open-my-storefront"
                >
                  Open my Storefront →
                </Link>
              )}
              {sub.dogs_limit !== -1 && sub.dogs_limit !== 0 && (
                <div className="mt-3 max-w-sm">
                  <div className="flex justify-between text-[11px] uppercase tracking-wider text-white/70 mb-1">
                    <span>Dogs this period</span>
                    <span>{sub.dogs_used || 0} / {sub.dogs_limit}</span>
                  </div>
                  <Progress
                    value={Math.min(100, ((sub.dogs_used || 0) / sub.dogs_limit) * 100)}
                    className="h-1.5 bg-white/10"
                  />
                </div>
              )}
            </div>
          )}
          {hasPass && (
            <div className="mt-3 flex items-center gap-2">
              <PawPrint size={18} weight="duotone" className="text-gold-light" />
              <span className="text-sm text-white/90">
                Buyer Pass active until {buyerPass.expires_at ? new Date(buyerPass.expires_at).toLocaleDateString() : "—"}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            onClick={onPortal}
            disabled={portalLoading}
            className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white hover:text-ink"
            data-testid="billing-portal-btn"
          >
            <CreditCard size={15} className="mr-1.5" />
            {portalLoading ? "Opening…" : "Manage payment"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/** ----------------------- RecommendedUpgrade ---------------------- */
function RecommendedUpgrade({ sub, buyerPass, onBuy, checkoutLoading }) {
  const currentTier = planTier(sub?.plan);
  const hasPass = buyerPass && !buyerPass.admin_override;

  // Promote next-step-up plan. If the user has nothing, recommend Buyer
  // Pass first (lowest-friction conversion). If they have basic, push
  // Pro. If they have Pro, push Elite. If they have Elite, nothing to
  // upsell.
  let recId;
  if (currentTier === 0 && !hasPass) recId = "buyer_pass";
  else if (currentTier === 0 && hasPass) recId = "basic_plan";
  else if (currentTier === 1) recId = "pro_plan";
  else if (currentTier === 2) recId = "kennel_elite";
  else recId = null;

  if (!recId) return null;
  const plan = SUBSCRIPTIONS.find((p) => p.id === recId);
  if (!plan) return null;
  const Icon = plan.icon;
  const annualSavings = Math.max(0, plan.monthly * 12 - plan.annual);
  const annualSavingsPct = plan.monthly > 0
    ? Math.round((annualSavings / (plan.monthly * 12)) * 100)
    : 0;
  // Show the first 3 benefits as a quick "what you get" preview —
  // helps the buyer connect the dollar amount to concrete value.
  const previewPerks = (plan.features || plan.perks || plan.includes || []).slice(0, 3);
  return (
    <div
      className="rounded-2xl border-2 border-gold bg-gradient-to-br from-[#FBF7EC] to-white p-6 mb-10 shadow-[0_24px_60px_rgba(201,162,39,0.18)]"
      data-testid="billing-recommended"
    >
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex-1 min-w-[240px]">
          <Badge className="rounded-full bg-gold-gradient text-ink border-0 font-semibold text-[10px] tracking-[0.18em] uppercase px-3 py-1 mb-3">
            <TrendUp size={11} weight="bold" className="mr-1" /> Recommended next step
          </Badge>
          <div className="flex items-center gap-3 mb-2">
            <Icon size={32} weight="duotone" className="text-gold-dark" />
            <h2 className="font-serif text-3xl text-ink">{plan.title}</h2>
          </div>
          <p className="text-sm text-[#4A4A4A] mb-3 max-w-lg">{plan.pitch}</p>
          {previewPerks.length > 0 && (
            <ul className="text-sm text-[#3d3d3d] mb-4 space-y-1.5" data-testid="billing-recommended-perks">
              {previewPerks.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle size={15} weight="fill" className="text-[#0F8043] mt-0.5 shrink-0" />
                  <span>{typeof p === "string" ? p : p.label || p.text}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="font-serif text-3xl text-ink mb-1">
            {fmtUSD(plan.monthly)}<span className="text-base text-[#6B6B6B] ml-1.5">/mo</span>
            <span className="text-sm text-[#6B6B6B] ml-3">or {fmtUSD(plan.annual)}/yr</span>
          </div>
          {annualSavings > 0 && (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0F8043]/10 text-[#0F8043] px-2.5 py-1 text-[11px] font-semibold tracking-wide mb-2"
              data-testid="billing-recommended-savings"
            >
              <Tag size={11} weight="bold" />
              Save {fmtUSD(annualSavings)}/yr ({annualSavingsPct}% off) when you pay annually
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 min-w-[200px]">
          <Button
            className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-12 font-semibold shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
            onClick={() => onBuy(plan.monthly_pkg)}
            disabled={!!checkoutLoading}
            data-testid={`billing-recommended-monthly`}
          >
            {checkoutLoading === plan.monthly_pkg ? "Loading…" : `Start monthly · ${fmtUSD(plan.monthly)}${isNativeApp() ? " — in browser" : ""}`}
          </Button>
          <Button
            className="rounded-full h-11 bg-[#0E0E0E] hover:bg-[#C1272D] text-white border-0 font-semibold"
            onClick={() => onBuy(plan.annual_pkg)}
            disabled={!!checkoutLoading}
            data-testid={`billing-recommended-annual`}
          >
            {checkoutLoading === plan.annual_pkg ? "Loading…" : `Pay annually · ${fmtUSD(plan.annual)}${isNativeApp() ? " — in browser" : ""}`}
            {annualSavingsPct > 0 && (
              <span className="ml-2 text-[10px] uppercase tracking-wider opacity-90 bg-white/15 rounded-full px-2 py-0.5">
                Save {annualSavingsPct}%
              </span>
            )}
          </Button>
          <div className="text-[11px] text-[#6B6B6B] text-center pt-1" data-testid="billing-recommended-trust">
            Cancel anytime · No hidden fees · Stripe-secure checkout
          </div>
        </div>
      </div>
    </div>
  );
}


/** --------------------------- PlanCard ---------------------------- */
function PlanCard({ plan, cadence, sub, buyerPass, onBuy, checkoutLoading, recommended }) {
  const Icon = plan.icon;
  const price = cadence === "annual" ? plan.annual : plan.monthly;
  const pkg = cadence === "annual" ? plan.annual_pkg : plan.monthly_pkg;
  const isCurrent =
    (plan.id === "buyer_pass" && buyerPass && !buyerPass.admin_override) ||
    (plan.id === "basic_plan" && sub?.plan === "basic") ||
    (plan.id === "pro_plan" && sub?.plan === "pro") ||
    (plan.id === "kennel_elite" && sub?.plan === "kennel_elite") ||
    (plan.id === "storefront" && sub?.plan === "storefront");

  return (
    <div
      className={`relative rounded-2xl border-2 bg-white p-7 flex flex-col transition-all duration-500 ${
        plan.highlight
          ? "border-[#C9A227] shadow-[0_20px_50px_rgba(201,162,39,0.15)]"
          : "border-stone-200 shadow-[0_8px_24px_rgba(0,0,0,0.04)]"
      } ${isCurrent ? "ring-2 ring-gold" : ""} ${
        recommended && !isCurrent
          ? "ring-2 ring-crimson ring-offset-2 ring-offset-[#FAFAFA] -translate-y-1 shadow-[0_20px_50px_rgba(193,39,45,0.25)]"
          : ""
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {recommended && !isCurrent && (
        <Badge
          className="absolute -top-3 left-6 rounded-full bg-crimson text-white border-0 font-semibold text-[10px] tracking-[0.18em] uppercase px-3 py-1 shadow-[0_4px_12px_rgba(193,39,45,0.35)]"
          data-testid={`plan-recommended-${plan.id}`}
        >
          ★ Recommended for you
        </Badge>
      )}
      {isCurrent && (
        <Badge
          className="absolute -top-3 left-6 rounded-full bg-ink text-white border-0 font-semibold text-[10px] tracking-[0.18em] uppercase px-3 py-1"
          data-testid={`plan-current-${plan.id}`}
        >
          Current plan
        </Badge>
      )}
      {plan.highlight && !isCurrent && !recommended && (
        <Badge
          className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#C9A227] text-white border-0 font-bold text-[10px] tracking-wider uppercase px-3 py-1"
        >
          Most popular
        </Badge>
      )}
      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4 border ${
        plan.highlight ? "bg-[#FBF7EC] border-[#C9A227]/40" : "bg-stone-50 border-stone-200"
      }`}>
        <Icon size={20} weight="duotone" className={plan.highlight ? "text-[#8A6A00]" : "text-ink/70"} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/50 mb-2">
        {plan.tag}
      </p>
      <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">{plan.title}</h3>
      <p className="text-[13px] text-ink/60 mt-1 leading-snug min-h-[2.4em] mb-4">{plan.pitch}</p>
      <div className="mb-5">
        <p className="font-spectral text-5xl font-light tracking-tight text-ink">
          ${Math.floor(price)}
          <span className="text-base font-sans text-ink/50">
            {price % 1 ? `.${Math.round((price % 1) * 100).toString().padStart(2, "0")}` : ""}
          </span>
          <span className="text-base font-sans text-ink/50 ml-1">/ {cadence === "annual" ? "year" : "month"}</span>
        </p>
        {cadence === "annual" && (
          <p className="text-[11px] text-emerald-700 font-semibold mt-1">
            Billed yearly — ${(price / 12).toFixed(2)}/mo equivalent
          </p>
        )}
      </div>
      <ul className="space-y-2.5 mb-5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] text-ink/80 leading-snug">
            <CheckCircle size={15} weight="duotone" className={`shrink-0 mt-0.5 ${plan.highlight ? "text-[#8A6A00]" : "text-ink/70"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.anchor && (
        <div
          className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 mb-4 text-[11.5px] text-ink/65 leading-snug"
          data-testid={`plan-anchor-${plan.id}`}
        >
          <span className="font-semibold text-ink/80">Tip · </span>{plan.anchor}
        </div>
      )}
      <Button
        className={`w-full rounded-full h-11 font-semibold ${
          isCurrent
            ? "bg-[#FAF7EE] text-ink border border-[#E8E0CC] hover:bg-[#FAF3DA]"
            : plan.highlight
            ? "bg-ink hover:bg-crimson text-white border-0"
            : "bg-white border border-ink/20 hover:border-gold text-ink"
        }`}
        disabled={isCurrent || !!checkoutLoading}
        onClick={() => onBuy(pkg)}
        data-testid={`plan-cta-${plan.id}`}
      >
        {isCurrent ? "Active" : checkoutLoading === pkg ? "Loading…" : `Get ${plan.title}${isNativeApp() ? " — in browser" : ""}`}
        {!isCurrent && <CaretRight size={13} weight="bold" className="ml-1" />}
      </Button>
    </div>
  );
}


/** -------------------------- OfferCard ----------------------------
 * Used for both the "Verification & prestige" tier (Get verified) and
 * the "À la carte" items.
 *
 * - Items without a `route` field trigger checkout immediately
 *   (non-contextual purchases like Verified Kennel Badge).
 * - Items with a `route` field navigate the user to the page where
 *   they can pick the target (dog, litter, event) and start checkout
 *   from there with the correct payload.
 * - Items that also have a `requires` field ("dog" | "litter") flip to
 *   an "Empty inventory" prompt when the user owns zero of that type
 *   — e.g. "Transfer ownership" cannot be done if you don't own a dog
 *   yet, so the card swaps to "Register a dog first →" linking to
 *   /dogs/new. Eliminates dead-end clicks for brand-new users.
 *   `counts` is `{ dogs: number|null, litters: number|null }`; `null`
 *   means we haven't loaded yet, so we render the normal CTA (counts
 *   fetch is best-effort and shouldn't block the page).
 */
function OfferCard({ item, onBuy, checkoutLoading, ctaLabel = "Buy now", counts }) {
  const Icon = item.icon;
  const navigate = useNavigate();
  const isRoute = !!item.route;

  // Empty-state branch: if this card needs an existing dog/litter and
  // the user has zero, override the route + CTA to point them at the
  // "Register" flow instead. We only know the count after the API
  // resolves (counts.dogs / counts.litters === 0); while loading
  // (null) we leave the card as-is.
  let effectiveRoute = item.route;
  let effectiveCta = item.cta || ctaLabel;
  let isEmptyPrompt = false;
  if (item.requires === "dog" && counts?.dogs === 0) {
    effectiveRoute = "/dogs/new";
    effectiveCta = "Register a dog first";
    isEmptyPrompt = true;
  } else if (item.requires === "litter" && counts?.litters === 0) {
    effectiveRoute = "/litters/new";
    effectiveCta = "Register a litter first";
    isEmptyPrompt = true;
  }

  return (
    <div
      className={`rounded-2xl border bg-white p-5 flex flex-col transition-all ${
        isEmptyPrompt
          ? "border-dashed border-[#D8D8D8] hover:border-gold/40"
          : "border-[#E8E0CC] hover:border-gold/60 hover:shadow-[0_18px_44px_rgba(201,162,39,0.10)]"
      }`}
      data-testid={`offer-card-${item.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <Icon size={24} weight="duotone" className={isEmptyPrompt ? "text-[#9A9A9A]" : "text-gold-dark"} />
        <div className="text-right">
          <div className={`font-serif text-xl font-medium ${isEmptyPrompt ? "text-[#9A9A9A]" : "text-ink"}`}>
            {fmtUSD(item.price)}
          </div>
          <div className="text-[10.5px] text-[#6B6B6B]">{item.cadence}</div>
        </div>
      </div>
      <h4 className={`font-serif text-base mb-1 ${isEmptyPrompt ? "text-[#6B6B6B]" : "text-ink"}`}>{item.title}</h4>
      <p className="text-[12.5px] text-[#4A4A4A] leading-relaxed mb-3 flex-1">{item.pitch}</p>
      {isEmptyPrompt && (
        <p className="text-[11.5px] text-gold-dark mb-2 italic" data-testid={`offer-empty-note-${item.id}`}>
          {item.requires === "dog"
            ? "You'll be able to use this once you register your first dog."
            : "You'll be able to use this once you register your first litter."}
        </p>
      )}
      <Button
        variant="outline"
        className={`w-full rounded-full h-9 font-medium text-[13px] ${
          isEmptyPrompt
            ? "border-gold-dark/60 text-gold-dark hover:bg-gold-gradient hover:text-ink hover:border-transparent"
            : "border-ink text-ink hover:bg-ink hover:text-white"
        }`}
        disabled={!!checkoutLoading && !isRoute}
        onClick={() => (isRoute ? navigate(effectiveRoute) : onBuy(item.pkg))}
        data-testid={`offer-cta-${item.id}`}
      >
        {!isRoute && checkoutLoading === item.pkg ? "Loading…" : `${effectiveCta}${!isRoute && isNativeApp() ? " — in browser" : ""}`}
        <CaretRight size={12} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}


/** Founding Elite scarcity tile — surfaces the 100-seat cap with a
 *  live counter pulled from `/api/pricing/founding-elite`. Renders
 *  nothing once seats are sold out so the section auto-collapses. */
function FoundingEliteTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};
  useEffect(() => {
    api.get("/pricing/founding-elite").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.is_sold_out) return null;
  const pct = Math.round((data.claimed / data.cap) * 100);
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#3F1D1F] bg-gradient-to-br from-[#1A0F10] via-[#2A1517] to-[#3F1D1F] text-white p-5 md:p-6 mt-6"
      data-testid="billing-founding-elite"
    >
      <div className="absolute top-3 right-4 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C9A227]">
        Limited · 100 seats
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C9A227] mb-2 inline-flex items-center gap-1.5">
            <Crown size={12} weight="fill"/> Founding Elite
          </p>
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
            All Kennel Elite features · <span className="text-[#C9A227]">$99</span><span className="text-base text-white/60">/mo</span>
          </h3>
          <p className="text-[13px] text-white/70 mt-1.5 max-w-xl leading-relaxed">
            Locked at $99/mo for 36 months. Founding Member wax-seal across your kennel + every listing.
          </p>
          <div className="mt-3">
            <div className="h-1.5 w-full max-w-md bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#C9A227]" style={{ width: `${Math.max(pct, 2)}%` }}/>
            </div>
            <p
              className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9A227] mt-1.5"
              data-testid="billing-founding-elite-counter"
            >
              {data.remaining} of {data.cap} seats remaining
            </p>
          </div>
        </div>
        <Button
          onClick={async () => {
            if (!user) {
              window.location.href = "/register?intent=founding_elite";
              return;
            }
            setBusy(true);
            try {
              const { data: co } = await api.post("/billing/checkout", {
                package_id: "kennel_elite_founding",
                origin_url: webOrigin(),
              });
              if (co.url) {
                window.location.href = co.url;
                return;
              }
              if (co.free) toast.success("Founding Elite activated on your account.");
            } catch (e) {
              toast.error(formatApiError(e));
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="rounded-full h-11 px-5 bg-[#C9A227] hover:bg-[#A8881E] text-ink font-bold shrink-0"
          data-testid="billing-founding-elite-cta"
        >
          {busy ? "Redirecting…" : "Claim a Founding seat →"}
        </Button>
      </div>
    </div>
  );
}

/** Pro trial + Gift cross-sell strip. Sits below the subscription
 *  grid. Renders even for logged-out users (CTA bounces to login). */
function ProTrialAndGiftStrip({ currentSub }) {
  // App Review 3.1.1: gift redemption codes and trial signup CTAs are
  // web-only. Hidden in the native shell.
  if (isNativeApp()) return null;
  const onProAlready = currentSub?.plan === "pro" || currentSub?.plan === "kennel_elite";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="billing-trial-gift-strip">
      {!onProAlready && (
        <div className="rounded-2xl border border-[#E5E5E5] bg-white p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Try Pro free</p>
          <h4 className="font-serif text-xl">14-day free Breeder Pro trial</h4>
          <p className="text-[13px] text-[#6B6B6B] mt-1.5 leading-relaxed">
            Card on file. No charge for 14 days. Cancel anytime — we email a reminder at day 11.
          </p>
          <Link to="/register?intent=pro_trial">
            <Button className="mt-3 rounded-full bg-ink hover:bg-crimson text-white h-10 px-5" data-testid="billing-pro-trial-cta">
              Start 14-day trial →
            </Button>
          </Link>
        </div>
      )}
      <div className="rounded-2xl border border-[#E8E0CC] bg-cream p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-2">For someone else</p>
        <h4 className="font-serif text-xl">Gift a subscription</h4>
        <p className="text-[13px] text-[#6B6B6B] mt-1.5 leading-relaxed">
          Pay once, hand them a redemption code. Buyer Pass · Dog Log · Breeder Basic · Breeder Pro.
        </p>
        <Link to="/gift">
          <Button variant="outline" className="mt-3 rounded-full h-10 px-5" data-testid="billing-gift-cta">
            Browse gift options →
          </Button>
        </Link>
      </div>
    </div>
  );
}

/** Dynamic add-ons grid backed by /api/public/prices/catalog. Renders
 *  every non-plan, non-verification SKU currently in PACKAGES. New SKUs
 *  auto-appear without a code change. */
function DynamicAddons({ onBuy, checkoutLoading, counts }) {
  const { byCategory, loading } = useBillingCatalog();
  const surfaced = [
    { key: "registry",    label: "Registry & certificates" },
    { key: "marketplace", label: "Marketplace listings" },
    { key: "breeding",    label: "Breeding directory" },
    { key: "upcoming",    label: "Upcoming litters" },
    { key: "buyer",       label: "Buyer add-ons" },
    { key: "owner",       label: "Owner add-ons" },
  ];
  if (loading) {
    return (
      <section className="mb-12" data-testid="billing-addons">
        <div className="h-32 rounded-2xl bg-stone-100 animate-pulse"/>
      </section>
    );
  }
  const cadenceFor = (card) => {
    if (card.kind === "plan") return card.days >= 300 ? "per year" : "per month";
    if (card.kind === "certificate") return "per dog";
    if (card.kind === "dog_registration") return "per dog";
    if (card.kind === "litter_registration") return "per litter";
    if (card.kind === "transfer_ownership") return "per transfer";
    if (card.kind === "video_tour") return "15-min";
    if (card.kind === "dog_marketplace_listing") return "60 days";
    if (card.kind === "litter_marketplace_listing") return "60 days";
    if (card.kind === "marketplace_feature") return "30 days";
    if (card.kind?.startsWith("breeding_listing")) return `${card.days || 30} days`;
    return card.days ? `${card.days} days` : "one-time";
  };
  const routeFor = (card) => {
    if (card.requires_dog) return "/dogs";
    if (card.requires_litter) return "/litters";
    if (card.kind === "transfer_ownership") return "/dogs";
    if (card.kind === "video_tour") return "/marketplace";
    if (card.kind === "dog_registration") return "/dogs/new";
    if (card.kind === "litter_registration") return "/litters/new";
    if (card.kind === "upcoming_litter_planned") return "/litters/upcoming/new";
    if (card.kind === "upcoming_litter_expecting") return "/litters/upcoming/new";
    return null;
  };
  const sections = surfaced
    .map((s) => ({ ...s, cards: byCategory[s.key] || [] }))
    .filter((s) => s.cards.length > 0);
  if (sections.length === 0) return null;
  return (
    <>
      {sections.map((s) => (
        <section className="mb-12" key={s.key} data-testid={`billing-addons-${s.key}`}>
          <div className="mb-5">
            <h2 className="font-serif text-2xl sm:text-3xl text-ink">{s.label}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {s.cards.map((card) => (
              <OfferCard
                key={card.package_id}
                item={{
                  id: card.package_id,
                  pkg: card.package_id,
                  icon: Certificate,
                  title: card.name,
                  price: card.amount ?? 0,
                  cadence: cadenceFor(card),
                  pitch: card.description,
                  route: routeFor(card),
                  cta: card.requires_dog ? "Pick a dog"
                       : card.requires_litter ? "Pick a litter"
                       : "Buy now",
                  requires: card.requires_dog ? "dog" : (card.requires_litter ? "litter" : undefined),
                }}
                onBuy={onBuy}
                checkoutLoading={checkoutLoading}
                counts={counts}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}


/** --------------------------- PromoBox ---------------------------- */
function PromoBox({ promo, setPromo }) {
  const [code, setCode] = useState(promo?.code || "");
  const [busy, setBusy] = useState(false);
  // App Review 3.1.1: no promo-code entry in the native shell.
  if (isNativeApp()) return null;

  async function apply() {
    if (!code.trim()) return;
    setBusy(true);
    try {
      // The backend validates the promo on checkout — we just stash
      // the code locally so every subsequent buy call passes it
      // through. No preview discount math here; checkout shows the
      // final amount on the Stripe page.
      setPromo({ code: code.trim().toUpperCase(), valid: true });
      toast.success("Promo will apply at checkout");
    } finally {
      setBusy(false);
    }
  }
  function clear() {
    setPromo(null);
    setCode("");
  }
  return (
    <div className="rounded-2xl border border-[#E8E0CC] bg-white p-5 mb-8" data-testid="billing-promo">
      <div className="flex items-center gap-2 mb-3">
        <Tag size={16} weight="duotone" className="text-gold-dark" />
        <h3 className="font-serif text-base text-ink">Have a promo code?</h3>
      </div>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="WELCOME10"
          className="rounded-full h-10"
          data-testid="billing-promo-input"
        />
        {promo?.valid ? (
          <Button variant="outline" onClick={clear} className="rounded-full h-10 px-5">
            Remove
          </Button>
        ) : (
          <Button
            onClick={apply}
            disabled={busy || !code.trim()}
            className="rounded-full h-10 bg-ink text-white hover:bg-crimson px-5"
            data-testid="billing-promo-apply"
          >
            Apply
          </Button>
        )}
      </div>
      {promo?.valid && (
        <p className="text-[12px] text-[#6B6B6B] mt-2">
          <CheckSquare size={12} className="inline mr-1 text-gold-dark" weight="fill" />
          Code <strong className="text-ink">{promo.code}</strong> will apply at checkout.
        </p>
      )}
    </div>
  );
}


/** -------------------------- TrustStrip -------------------------- */
function TrustStrip() {
  return (
    <section className="rounded-2xl bg-white border border-[#E8E0CC] p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="billing-trust-strip">
      {[
        { icon: ShieldCheck, label: "Stripe-secured", sub: "PCI-compliant checkout" },
        { icon: Heart, label: "7-day refund", sub: "Money-back guarantee" },
        { icon: TrendUp, label: "Cancel anytime", sub: "No long-term contracts" },
        { icon: Star, label: "200+ kennels", sub: "trust Armstrong K9" },
      ].map((it) => (
        <div key={it.label} className="flex items-center gap-3">
          <it.icon size={22} weight="duotone" className="text-gold-dark flex-shrink-0" />
          <div>
            <div className="text-[13px] font-medium text-ink leading-tight">{it.label}</div>
            <div className="text-[11px] text-[#6B6B6B] leading-tight">{it.sub}</div>
          </div>
        </div>
      ))}
    </section>
  );
}


/** ----------------------- PersonaShortcut ----------------------- */
/**
 * Quick "Who are you?" picker that highlights the most relevant plans
 * for each persona and scrolls them into view. Plays the role of a
 * 0-friction quiz without making the user fill anything out.
 */
function PersonaShortcut({ persona, onChange }) {
  const personas = [
    { id: "buyer",             label: "I want to buy a dog",                icon: HouseSimple, plan: "buyer_pass" },
    { id: "breeder",           label: "I'm a breeder",                      icon: Trophy,      plan: "pro_plan" },
    { id: "owner",             label: "I just love my dog",                 icon: Heart,       plan: "care_plus_dog_log" },
    { id: "service_provider",  label: "I'm a trainer / groomer / walker / sitter", icon: Sparkle, plan: null, link: "/become-a-provider" },
    { id: "storefront_seller", label: "I sell food, merch, or services",    icon: ShieldCheck, plan: "dog_shop_subscription" },
  ];
  const pick = (p) => {
    onChange(p.id);
    if (p.link) {
      window.location.href = p.link;
      return;
    }
    setTimeout(() => {
      const target = p.plan && document.querySelector(`[data-testid="plan-card-${p.plan}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };
  return (
    <section className="mb-8" data-testid="billing-persona">
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={14} weight="fill" className="text-gold" />
        <h2 className="text-[12px] font-bold uppercase tracking-[0.2em] text-ink">Quick pick — what fits you?</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {personas.map((p) => {
          const active = persona === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p)}
              data-testid={`persona-${p.id}`}
              className={`group rounded-2xl border p-4 text-left transition-all ${
                active
                  ? "bg-ink text-white border-ink shadow-[0_18px_36px_-18px_rgba(14,14,14,0.5)]"
                  : "bg-white border-[#E8E0CC] hover:border-gold/60 hover:-translate-y-[2px]"
              }`}
            >
              <p.icon size={20} weight="duotone" className={`mb-2 ${active ? "text-gold-light" : "text-gold-dark"}`} />
              <div className={`font-spectral text-base leading-tight ${active ? "text-white" : "text-ink"}`}>
                {p.label}
              </div>
              <div className={`text-[11px] mt-1 ${active ? "text-white/70" : "text-[#6B6B6B]"}`}>
                {p.link ? "Apply to start booking →" : "Recommended plan highlighted below ↓"}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}


/** -------------------- PlanComparisonTable ----------------------- */
/**
 * Honest side-by-side of the 5 subscription tiers across the rows
 * buyers actually shopping for plans care about. Kept lean — only
 * decision-driving features make the cut. Hidden by default behind a
 * "Compare all plans" toggle so it doesn't dominate the page; opens
 * with a clean focus shift for power users.
 */
function PlanComparisonTable() {
  const [open, setOpen] = useState(false);
  const { prices } = usePublicPrices();
  const features = [
    { label: "Dog registrations / year", values: ["—", "—", "5", "Unlimited", "Unlimited"] },
    { label: "Marketplace listings", values: ["—", "—", "Single dogs free", "Unlimited (single + litter)", "Unlimited"] },
    { label: "Buyer inquiries", values: ["3 / mo", "Unlimited", "Receive only", "Receive only", "Receive only"] },
    { label: "6-gen pedigree view", values: ["—", "✓", "✓", "✓", "✓"] },
    { label: "Pedigree certificates", values: ["—", "—", `${prices("dog_registration", "$25")} each`, "Free", "Free"] },
    { label: "Verified badge in marketplace", values: ["—", "—", "—", "✓", "✓"] },
    { label: "Dog Log (10-category care tracker)", values: ["—", "—", "—", "✓ free", "✓ free"] },
    { label: "Heat-cycle tracking", values: ["—", "—", "✓", "✓", "✓"] },
    { label: "Staff sub-accounts", values: ["—", "—", "—", "—", "Up to 3"] },
    { label: "API access", values: ["—", "—", "—", "—", "✓"] },
    { label: "White-label kennel page", values: ["—", "—", "—", "—", "✓"] },
  ];
  const tiers = [
    { name: "Free",        price: "$0" },
    { name: "Buyer Pass",  price: `${prices("buyer_pass_monthly", "$4.99")}/mo` },
    { name: "Basic",       price: `${prices("basic_plan_v2", "$19")}/mo` },
    { name: "Pro",         price: `${prices("pro_plan_v2", "$44")}/mo`, highlight: true },
    { name: "Elite",       price: `${prices("kennel_elite_v2", "$129")}/mo` },
  ];
  return (
    <section className="mb-12" data-testid="billing-compare-table">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-medium text-ink hover:text-crimson transition-colors mb-3"
        data-testid="compare-toggle"
      >
        <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {open ? "Hide" : "Compare"} all plans side-by-side
      </button>
      {open && (
        <div className="rounded-2xl border border-[#E8E0CC] bg-white overflow-x-auto">
          <table className="w-full text-[13px] min-w-[760px]">
            <thead className="bg-[#FAF3DA]">
              <tr>
                <th className="py-3 px-4 text-left font-medium text-ink">Feature</th>
                {tiers.map((t) => (
                  <th key={t.name} className={`py-3 px-3 text-center font-medium ${t.highlight ? "text-gold-dark" : "text-ink"}`}>
                    <div className="font-spectral text-base">{t.name}</div>
                    <div className="text-[11px] text-[#6B6B6B] font-normal mt-0.5">{t.price}</div>
                    {t.highlight && <div className="text-[9px] uppercase tracking-[0.18em] mt-1 text-gold-dark">Most popular</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EDEDED]">
              {features.map((f) => (
                <tr key={f.label} className="hover:bg-cream/40">
                  <td className="py-2.5 px-4 text-[#3a3a3a]">{f.label}</td>
                  {f.values.map((v, i) => (
                    <td key={i} className={`py-2.5 px-3 text-center ${tiers[i].highlight ? "bg-[#FBF7EC]" : ""}`}>
                      {v === "—" ? (
                        <X size={13} className="text-[#C5C5C5] mx-auto" />
                      ) : v === "✓" ? (
                        <CheckCircle size={14} weight="fill" className="text-emerald-600 mx-auto" />
                      ) : (
                        <span className="text-[12px] text-ink">{v}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


/** -------------------------- BillingFaq -------------------------- */
function BillingFaq() {
  const { prices } = usePublicPrices();
  const dogReg = prices("dog_registration", "$25");
  const kennelVer = prices("kennel_verification", "$99");
  const faqs = [
    {
      q: "Can I cancel any time?",
      a: "Yes. All subscriptions cancel at the end of the current paid period — no future charges. We don't issue prorated refunds, but you keep full access until period close. Cancel right here on /billing → 'Manage payment' → Cancel subscription.",
    },
    {
      q: "Can I switch plans?",
      a: "Yes. Upgrades take effect immediately and Stripe pro-rates the difference. Downgrades take effect at your next renewal so you don't lose what you've already paid for.",
    },
    {
      q: "What if I need more registrations than my plan allows?",
      a: `Basic includes 5 dog registrations/year (${dogReg} of value each). Pro and Elite include unlimited. You can also buy single dog registrations à la carte at any time.`,
    },
    {
      q: "Is the Verified badge worth it?",
      a: `Buyers filter by Verified — listings without it average 70% fewer inquiries. The one-time ${kennelVer} verification fee is bundled free with Breeder Pro and Kennel Elite.`,
    },
    {
      q: "What's Dog Log and is it really free with Pro?",
      a: "Dog Log ($9.99/mo standalone) is a 10-category care tracker for everyday owners — meals, walks, weight, meds, symptoms, vet PDFs, and a live sitter-sharing portal. It's bundled free with Breeder Pro & Kennel Elite, so your kennel staff and your personal pets all benefit.",
    },
    {
      q: "Do you offer prorated refunds?",
      a: "No. Once a subscription period or service is paid, we don't refund unused time. Our 7-day money-back guarantee covers buyer's remorse within the first week of a brand-new subscription only.",
    },
  ];
  return (
    <section className="mb-12" data-testid="billing-faq">
      <h2 className="font-serif text-2xl sm:text-3xl text-ink mb-1">Common questions</h2>
      <p className="text-[13px] text-[#6B6B6B] mb-5">Click to expand each answer.</p>
      <div className="space-y-2">
        {faqs.map((f, i) => (
          <details
            key={i}
            className="rounded-xl border border-[#E8E0CC] bg-white open:shadow-[0_8px_24px_-12px_rgba(14,14,14,0.08)] transition-shadow"
            data-testid={`faq-${i}`}
          >
            <summary className="cursor-pointer px-5 py-3 font-medium text-ink text-[14px] flex items-center justify-between gap-3 list-none">
              {f.q}
              <CaretDown size={13} weight="bold" className="text-[#6B6B6B] transition-transform group-open:rotate-180" />
            </summary>
            <p className="px-5 pb-4 text-[13px] text-[#3a3a3a] leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}


/** ------------------------ CancellationHelp ---------------------- */
function CancellationHelp({ sub, onPortal, portalLoading }) {
  // No active subscription → static help. Active → big "Manage / cancel" button.
  return (
    <section className="rounded-2xl border border-[#E8E0CC] bg-cream/50 p-6 mb-10" data-testid="billing-cancel-help">
      <div className="flex items-start gap-4 flex-wrap">
        <Info size={22} weight="duotone" className="text-gold-dark flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-spectral text-lg text-ink mb-1">Need to cancel?</h3>
          <p className="text-[13px] text-[#3a3a3a] leading-relaxed">
            All registry subscriptions can be cancelled right here from <strong>/billing</strong>. Click <strong>Manage payment</strong> on your active subscription to open the Stripe portal — cancel auto-renewal, update your card, download invoices, or switch plans, all in one place. Cancellation stops future billing immediately; your access continues until the current period closes. <Link to="/legal/refund" className="underline decoration-gold underline-offset-2 hover:text-ink">No prorated refunds</Link>.
          </p>
        </div>
        {sub?.subscription_id && (
          <Button
            disabled={portalLoading}
            onClick={onPortal}
            className="rounded-full bg-ink hover:bg-crimson text-white h-10 px-5 text-[13px]"
            data-testid="cancel-help-portal-btn"
          >
            {portalLoading ? "Opening…" : "Manage / cancel"}
          </Button>
        )}
      </div>
    </section>
  );
}


/** ============================== BILLING ========================== */
export default function Billing() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  // Native shell: iOS always redirects to the IAP subscription screen —
  // App Review flagged the add-on catalog (3.1.1: digital SKUs without
  // IAP parity may not be sold or advertised in the iOS app). Android
  // keeps the previous behaviour (US accounts see the full page).
  useEffect(() => {
    if (!isNativeApp()) return;
    if (isIOS()) {
      navigate("/account/subscription", { replace: true });
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        if (r.data?.country !== "US") navigate("/account/subscription", { replace: true });
      })
      .catch(() => navigate("/account/subscription", { replace: true }));
  }, [navigate]);
  const [searchParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [buyerPass, setBuyerPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const [cadence, setCadence] = useState("monthly");
  const [persona, setPersona] = useState(null); // buyer | breeder | owner

  // ------ Load active subscription + inventory counts ------
  // We need dog/litter counts to drive the empty-state UX on the
  // contextual À la carte cards (e.g. "Pick a dog to list" makes no
  // sense if the user owns zero dogs — flip it to "Register one first").
  const [counts, setCounts] = useState({ dogs: null, litters: null });
  const [renewalPreviews, setRenewalPreviews] = useState([]);

  useEffect(() => {
    api
      .get("/billing/my-subscription")
      .then((s) => {
        setSub(s.data.subscription);
        setBuyerPass(s.data.buyer_pass);
      })
      .finally(() => setLoading(false));
    // Renewal-notice preview — best-effort, silent on failure.
    api
      .get("/billing/renewal-preview")
      .then((r) => setRenewalPreviews(r.data?.previews || []))
      .catch(() => setRenewalPreviews([]));
    // Load counts in parallel — best-effort, failures are silent
    // because the buttons still work (they just lose the smart routing).
    Promise.all([
      api.get("/dogs").then((r) => (Array.isArray(r.data) ? r.data.length : 0)).catch(() => null),
      api.get("/litters").then((r) => (Array.isArray(r.data) ? r.data.length : 0)).catch(() => null),
    ]).then(([d, l]) => setCounts({ dogs: d, litters: l }));
  }, []);

  // ------ Deep-link support: ?plan=pro_plan etc. ------
  useEffect(() => {
    const want = searchParams.get("plan");
    if (!want) return;
    // CSS.escape() guards against any future ?plan= value containing
    // selector-special characters. Today's producers are all safe
    // testid strings, but this is a one-line hardening.
    const esc = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(want) : want;
    const id = setTimeout(() => {
      document
        .querySelector(`[data-testid="plan-card-${esc}"], [data-testid="offer-card-${esc}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    return () => clearTimeout(id);
  }, [searchParams]);

  const buy = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const payload = { package_id: packageId, origin_url: webOrigin() };
      if (promo?.valid) payload.promo_code = promo.code;
      // Buyer Pass kennel-referral attribution — only consume the
      // referrer marker when actually subscribing to Buyer Pass.
      if (packageId.startsWith("buyer_pass_")) {
        try {
          const m = await import("@/lib/referral-attribution");
          const ref = m.consumeReferrer();
          if (ref) payload.referrer_kennel_id = ref;
        } catch { /* best-effort */ }
      }
      const { data } = await api.post("/billing/checkout", payload);
      if (data.free) {
        toast.success(
          data.admin_override
            ? "Free for admin — activated"
            : data.already
            ? "Already unlocked"
            : "Unlocked"
        );
        window.location.reload();
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckoutLoading("");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal-session", {
        return_url: window.location.href,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Make a purchase first to unlock payment management.");
      setPortalLoading(false);
    }
  };

  const annualSavings = useMemo(() => {
    return SUBSCRIPTIONS.reduce((acc, p) => {
      const yearlyOfMonthly = p.monthly * 12;
      const pct = Math.round(((yearlyOfMonthly - p.annual) / yearlyOfMonthly) * 100);
      acc[p.id] = pct;
      return acc;
    }, {});
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-5 py-12 text-center text-[#6B6B6B]">
          Loading your subscription…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="billing-page">
        {/* Reader-app banner — surfaces only inside the Capacitor
            iOS/Android shell. Hides buy CTAs and points users to
            armstrongk9registry.com to manage their subscription. Required
            by Apple's "reader app" rule to avoid the 30% IAP cut. */}
        <ReaderAppBanner />
        <IapPaywall />
        {/* Payment recovery banner — auto-renders when any sub is past_due */}
        <PaymentRecoveryBanner />
        {/* Comp on file — auto-hides for users with no active comp. */}
        <div className="mb-4"><CompBanner /></div>
        {/* Referral credits — auto-hides when user has zero credits.
            Renders ABOVE pricing tiers so an earned month is visible
            when the user is staring at "subscribe" buttons. */}
        <div className="mb-6"><ReferralCreditsCard /></div>
        {/* HERO */}
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
            Billing & subscriptions
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink">
            Pay for what you use. Upgrade any time.
          </h1>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl text-sm sm:text-base">
            Browse <Link to="/services" className="underline decoration-gold underline-offset-4 hover:text-ink">all services</Link> and <Link to="/features" className="underline decoration-gold underline-offset-4 hover:text-ink">every platform feature</Link> any time. Cancellations refund nothing prorated — but our 7-day money-back guarantee covers anyone who changes their mind.
          </p>
        </div>

        {/* TRUST STRIP */}
        <TrustStrip />

        {/* PERSONA QUICK PICK */}
        <PersonaShortcut persona={persona} onChange={setPersona} />

        {/* ACTIVE SUBSCRIPTION */}
        <ActiveSubscriptionBanner
          sub={sub}
          buyerPass={buyerPass}
          onPortal={openPortal}
          portalLoading={portalLoading}
        />

        {/* SAVINGS OFFERS — annual switch + per-dog plan math */}
        <SavingsOffers onBuy={buy} checkoutLoading={checkoutLoading} />

        {/* RENEWAL NOTICE PREVIEW — ROSCA / CA Auto-Renewal Law transparency */}
        <RenewalNoticePreview
          previews={renewalPreviews}
          onCancel={openPortal}
          portalLoading={portalLoading}
        />

        {/* RECOMMENDED NEXT STEP */}
        <RecommendedUpgrade
          sub={sub}
          buyerPass={buyerPass}
          onBuy={buy}
          checkoutLoading={checkoutLoading}
        />

        {/* PROMO */}
        <PromoBox promo={promo} setPromo={setPromo} />

        {/* SUBSCRIPTIONS */}
        <section className="mb-12" data-testid="billing-subscriptions">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">Plans &amp; subscriptions</p>
              <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight text-ink">Recurring plans</h2>
              <p className="text-[13px] text-ink/60 mt-1">Cancel any time. Annual saves up to {Math.max(...Object.values(annualSavings))}%.</p>
            </div>
            <div className="inline-flex flex-col items-end gap-1.5">
              <div className="inline-flex rounded-full bg-white border border-stone-200 p-1 shadow-sm" data-testid="billing-cadence-toggle">
                <button
                  type="button"
                  data-testid="billing-cadence-monthly"
                  className={`px-5 py-2 text-[13px] font-semibold rounded-full transition-all ${
                    cadence === "monthly" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                  }`}
                  onClick={() => setCadence("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  data-testid="billing-cadence-annual"
                  className={`px-5 py-2 text-[13px] font-semibold rounded-full transition-all relative ${
                    cadence === "annual" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                  }`}
                  onClick={() => setCadence("annual")}
                >
                  Annual
                  <span className="ml-2 inline-block bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                    Save up to {Math.max(...Object.values(annualSavings))}%
                  </span>
                </button>
              </div>
              {cadence === "monthly" && (
                <p
                  className="text-[11px] text-ink/50 italic"
                  data-testid="billing-monthly-nudge"
                >
                  Most members start monthly — switch any time, no penalty.
                </p>
              )}
            </div>
          </div>
          {/* ROSCA / CA Auto-Renewal Law clear-and-conspicuous notice.
              Required before charging for any auto-renewing plan;
              repeated inline near the Subscribe CTAs so a user can
              never plausibly say "I never saw the disclosure." */}
          <p
            className="text-[12px] text-[#6B6B6B] mb-4 leading-relaxed"
            data-testid="billing-auto-renew-microcopy"
          >
            By subscribing you agree to our{" "}
            <Link to="/auto-renew" className="underline decoration-gold underline-offset-2 hover:text-ink">
              Auto-Renewal Disclosure
            </Link>
            : the selected plan auto-renews until you cancel, and we'll
            email a reminder to your account email <strong>7 business days</strong>
            {" "}before each charge.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SUBSCRIPTIONS.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                cadence={cadence}
                sub={sub}
                buyerPass={buyerPass}
                onBuy={buy}
                checkoutLoading={checkoutLoading}
                recommended={
                  (persona === "buyer"   && p.id === "buyer_pass") ||
                  (persona === "breeder" && p.id === "pro_plan") ||
                  (persona === "owner"   && p.id === "care_plus_dog_log")
                }
              />
            ))}
          </div>
          {/* Conversion levers from the 2026-02 pricing audit. Each
              renders independently so the page degrades gracefully if
              one endpoint is down. */}
          <FoundingEliteTile />
          <ProTrialAndGiftStrip currentSub={sub} />
        </section>

        {/* VERIFICATION & TRUST */}
        <section className="mb-12" data-testid="billing-verification">
          <div className="mb-5">
            <h2 className="font-serif text-2xl sm:text-3xl text-ink">Verification & prestige</h2>
            <p className="text-[13px] text-[#6B6B6B] mt-1">Buyers trust verified kennels — and the registry ranks you higher in search.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {VERIFICATION.map((i) => (
              <OfferCard key={i.id} item={i} onBuy={buy} checkoutLoading={checkoutLoading} ctaLabel="Get verified" />
            ))}
          </div>
        </section>

        {/* ADD-ONS — dynamic. Reads from the public-prices catalog so
            any new SKU appearing in PACKAGES auto-renders here. The
            legacy hardcoded ADDONS array is no longer rendered. */}
        <DynamicAddons onBuy={buy} checkoutLoading={checkoutLoading} counts={counts} />

        {/* ADD-ONS rendered via <DynamicAddons /> above. Section removed
            2026-02 — the hardcoded ADDONS array was prone to drift
            whenever a new SKU shipped. */}

        {/* PLAN COMPARISON TABLE */}
        <PlanComparisonTable />

        {/* FAQ */}
        <BillingFaq />

        {/* CANCELLATION HELP */}
        <CancellationHelp sub={sub} onPortal={openPortal} portalLoading={portalLoading} />

        {/* FOOTER CALLOUT */}
        <section className="rounded-2xl bg-ink text-white p-7 text-center" data-testid="billing-footer-cta">
          <h3 className="font-serif text-2xl mb-2">Looking for the full feature list?</h3>
          <p className="text-white/75 max-w-md mx-auto mb-5 text-sm">
            Every platform feature — what makes us different and our complete inventory — lives on one page.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-11 px-6 font-semibold shadow-[0_8px_24px_rgba(201,162,39,0.35)]">
              <Link to="/features" data-testid="billing-features-cta">See platform features</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-ink h-11 px-6">
              <Link to="/services" data-testid="billing-services-cta">All services</Link>
            </Button>
          </div>
          {user?.role === "admin" && (
            <p className="text-[11px] text-white/40 mt-4">
              Admin tip — manage promo codes at <Link to="/admin/promo-codes" className="underline">/admin/promo-codes</Link>.
            </p>
          )}
        </section>
      </div>
    </AppShell>
  );
}
